import React from "react";
import {
  Box, Paper, Typography, alpha, Chip,
} from "@mui/material";
import InstallMobileIcon from "@mui/icons-material/InstallMobile";
import TvIcon from "@mui/icons-material/Tv";
import AndroidIcon from "@mui/icons-material/Android";
import {
  NAVY, GOLD, BORDER, TEXT_PRIMARY, TEXT_SECONDARY,
} from "../../theme/tokens";

const InstallationGuide: React.FC = () => (
  <Box sx={{ p: 3 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
      <Box sx={{
        width: 40, height: 40, borderRadius: 1.5,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: `linear-gradient(135deg, ${alpha("#7c3aed", 0.18)} 0%, ${alpha("#7c3aed", 0.06)} 100%)`,
        color: "#7c3aed",
      }}>
        <InstallMobileIcon />
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography fontSize={20} fontWeight={800} color={NAVY} letterSpacing="-0.02em">
          Installation Guide
        </Typography>
        <Typography fontSize={12} color={TEXT_SECONDARY}>
          Step-by-step instructions for every supported display platform. Print this page or send the URL to the on-site installer.
        </Typography>
      </Box>
    </Box>

    <Section icon={<AndroidIcon />} accent="#3DDC84" title="Android TV / Google TV / T95 Box">
      <Numbered items={[
        "Sideload the latest VM Display APK (or install from the device's app store if available).",
        "Open the VM Display Player app from the launcher.",
        "On the Setup screen, enter the Device Code shown in the admin panel (e.g. TV001). It is the SAME as the registration code — no separate code to type.",
        "Enter the API base URL from your admin panel (default: https://display.your-domain.com/api/).",
        "Tap Connect. The player registers, downloads the assigned playlist, and starts looping content within ~60 seconds.",
        "On boot, the app auto-launches via BootReceiver. No further action needed.",
      ]} />
      <Note>
        If you don't see the app on next boot, your TV OS likely has auto-launch
        disabled by default. Enable Settings → Apps → VM Display Player →
        "Auto start" (label varies by OEM).
      </Note>
    </Section>

    <Section icon={<TvIcon />} accent="#A50034" title="LG webOS Signage (commercial signage)">
      <Numbered items={[
        "On the LG signage TV, open the Web Browser app.",
        "Navigate to your player URL: https://display.your-domain.com/player/TV001",
        "Add the URL as the default browser homepage so it reloads on boot.",
        "In Signage Settings, set the input source to Browser and enable Auto-launch.",
        "The Web Player runs in fullscreen automatically and loops content. No further config needed.",
      ]} />
      <Note>
        Older webOS firmware (3.x and below) may need the URL whitelisted in
        the Signage SmartShare config first.
      </Note>
    </Section>

    <Section icon={<TvIcon />} accent="#1428A0" title="Samsung Tizen Smart TV">
      <Numbered items={[
        "Open the Internet browser app.",
        "Navigate to https://display.your-domain.com/player/TV001",
        "Add to home / pin the URL launcher if your Tizen build supports it.",
        "For Samsung B2B signage (MagicInfo), use the URL Launcher with the player URL.",
      ]} />
    </Section>

    <Section icon={<AndroidIcon />} accent="#FF9900" title="Amazon Fire TV Stick">
      <Numbered items={[
        "Enable Settings → My Fire TV → Developer Options → Apps from Unknown Sources.",
        "Install the Downloader app from the Amazon Appstore.",
        "Inside Downloader, enter the direct APK URL (your admin will provide).",
        "After the APK installs, open VM Display Player from the launcher.",
        "Enter the Device Code and API URL — same flow as Android TV.",
      ]} />
    </Section>

    <Section icon={<TvIcon />} accent="#0EA5E9" title="Windows / Chromebook / Generic browser display">
      <Numbered items={[
        "Open Chrome / Edge / Firefox on the display machine.",
        "Navigate to your player URL.",
        "Press F11 for fullscreen. Set the URL as the browser's homepage / startup tab.",
        "Optionally: enable kiosk mode via the OS (Windows Assigned Access, ChromeOS Managed Guest).",
      ]} />
    </Section>
  </Box>
);

const Section: React.FC<{
  icon: React.ReactElement; accent: string; title: string; children: React.ReactNode;
}> = ({ icon, accent, title, children }) => (
  <Paper elevation={0} sx={{
    mb: 3, borderRadius: 3, border: `1px solid ${BORDER}`, overflow: "hidden", bgcolor: "#fff",
  }}>
    <Box sx={{
      background: `linear-gradient(135deg, ${alpha(accent, 0.08)} 0%, ${alpha(accent, 0.02)} 100%)`,
      borderBottom: `1px solid ${alpha(accent, 0.18)}`,
      px: 3, py: 2, display: "flex", alignItems: "center", gap: 1.5,
    }}>
      <Box sx={{
        width: 32, height: 32, borderRadius: 1.5,
        display: "flex", alignItems: "center", justifyContent: "center",
        bgcolor: alpha(accent, 0.18), color: accent,
      }}>
        {React.cloneElement(icon, { sx: { fontSize: 18 } })}
      </Box>
      <Typography sx={{ fontSize: 15, fontWeight: 800, color: NAVY }}>{title}</Typography>
    </Box>
    <Box sx={{ p: 3 }}>{children}</Box>
  </Paper>
);

const Numbered: React.FC<{ items: string[] }> = ({ items }) => (
  <Box component="ol" sx={{ m: 0, pl: 0, listStyle: "none" }}>
    {items.map((t, i) => (
      <Box component="li" key={i} sx={{ display: "flex", gap: 2, py: 1.25,
        borderBottom: i < items.length - 1 ? `1px solid ${BORDER}` : "none" }}>
        <Chip size="small" label={i + 1}
          sx={{ height: 22, width: 22, fontSize: 11, fontWeight: 800,
            bgcolor: GOLD, color: "#0b1e2d" }} />
        <Typography sx={{ fontSize: 13.5, color: TEXT_PRIMARY, flex: 1 }}>
          {t}
        </Typography>
      </Box>
    ))}
  </Box>
);

const Note: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{
    mt: 2, p: 2, borderRadius: 2,
    bgcolor: alpha("#3b82f6", 0.06),
    border: `1px solid ${alpha("#3b82f6", 0.18)}`,
  }}>
    <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY }}>
      <strong style={{ color: "#1e40af" }}>Note: </strong>{children}
    </Typography>
  </Box>
);

export default InstallationGuide;
